import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, Check } from "lucide-react";
import { AuthShell } from "@/components/AuthShell";
import { Field } from "@/pages/auth/Login";
import { guestApi, isTransportError, USE_FIXTURES } from "@/lib/api";
import { toast } from "sonner";

export const Register = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirm: "",
  });
  const [errors, setErrors] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Your name, please.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Enter a valid email.";
    if (form.password.length < 8)
      errs.password = "At least 8 characters, mix of letters and numbers.";
    if (form.password !== form.confirm) errs.confirm = "Passwords don't match.";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSubmitting(true);
    try {
      const res = await guestApi.register({
        name: form.name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        password_confirmation: form.confirm,
      });
      toast.success(res?.message || "Account created — verify your email to continue.");
    } catch (err) {
      if (USE_FIXTURES && isTransportError(err)) {
        toast.info("Fixture mode: use OTP 111111.");
        // fall through to OTP screen only when fixtures are explicitly enabled
      } else {
        const laravelErrors = err?.response?.data?.errors || err?.apiErrors || {};
        const mapped = {};
        for (const [k, v] of Object.entries(laravelErrors)) {
          mapped[k] = Array.isArray(v) ? v[0] : v;
        }
        if (!Object.keys(mapped).length) {
          const msg =
            err?.response?.data?.message || err?.apiMessage || err?.message || "Registration failed.";
          toast.error(msg);
        } else {
          setErrors(mapped);
        }
        setSubmitting(false);
        return;
      }
    }
    navigate(`/verify-email-otp?email=${encodeURIComponent(form.email)}`);
    setSubmitting(false);
  };

  return (
    <AuthShell
      eyebrow="Register · Guest account"
      title={<>Create an<br /><span className="italic">account</span>.</>}
      subtitle="Ten seconds to sign up. We'll send a code to your email to confirm it's you."
      image="https://images.unsplash.com/photo-1668438712649-ffd85f756de5?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85"
      imageCaption="Warehouse 08 · Brooklyn, New York"
    >
      <form onSubmit={onSubmit} className="space-y-6" data-testid="register-form">
        <Field label="Full name" value={form.name} onChange={(v) => set("name", v)} testId="register-name" />
        {errors.name && <p className="text-xs text-primary">{errors.name}</p>}

        <Field label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} testId="register-email" />
        {errors.email && <p className="text-xs text-primary">{errors.email}</p>}

        <Field label="Phone (optional)" type="tel" value={form.phone} onChange={(v) => set("phone", v)} testId="register-phone" />

        <Field
          label="Password"
          type={showPw ? "text" : "password"}
          value={form.password}
          onChange={(v) => set("password", v)}
          testId="register-password"
          rightSlot={
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="text-muted-foreground hover:text-foreground"
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
        />
        {errors.password && <p className="text-xs text-primary">{errors.password}</p>}

        <Field
          label="Confirm password"
          type={showPw ? "text" : "password"}
          value={form.confirm}
          onChange={(v) => set("confirm", v)}
          testId="register-confirm"
        />
        {errors.confirm && <p className="text-xs text-primary">{errors.confirm}</p>}

        <ul className="space-y-2 text-xs text-muted-foreground">
          <li className="flex items-center gap-2">
            <Check size={12} className="text-primary" /> 8+ characters
          </li>
          <li className="flex items-center gap-2">
            <Check size={12} className="text-primary" /> Mix letters and numbers
          </li>
        </ul>

        <p className="text-xs text-muted-foreground">
          By creating an account you agree to our{" "}
          <Link to="/terms-and-conditions" className="underline">Terms</Link> and{" "}
          <Link to="/booking-policies" className="underline">Booking Policies</Link>.
        </p>

        <button
          type="submit"
          data-testid="register-submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-medium text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
        >
          {submitting ? "Creating…" : "Create account"} <ArrowRight size={16} />
        </button>

        <p className="pt-2 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-foreground underline">
            Log in
          </Link>
        </p>
      </form>
    </AuthShell>
  );
};

export default Register;
